import { NgClass } from '@angular/common';
import { Component, computed, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDivider } from '@angular/material/divider';
import { MatError } from '@angular/material/input';
import { VirtualFenceService } from '@vectronic/base-map';
import { userDtos, webDtos } from '@vectronic/openApi-customer';
import { SharedMapPageComponent } from '@vectronic/shared-map-page';
import { isEqual } from 'es-toolkit';
import { distinctUntilChanged, of } from 'rxjs';
import { RemoteCommandProviderService } from '../../services/remote-command-provider.service';

@Component({
  selector: 'rc-virtual-fence-view',
  templateUrl: './virtual-fence-view.component.html',
  styleUrls: ['./virtual-fence-view.component.scss'],
  imports: [SharedMapPageComponent, NgClass, MatDivider, MatError],
})
export class VirtualFenceViewComponent implements OnInit, OnDestroy {
  virtualFenceService = inject(VirtualFenceService);
  remoteCommandProviderService = inject(RemoteCommandProviderService, { optional: true });

  isPositionsLoading = input<boolean>(false);
  hideConstructionRules = input<boolean>(false);
  mapPositions = input<webDtos.MapPositionsList[]>([]);
  userPreferences = input<userDtos.UserPreferences>();
  lastXRecords = input<number>();

  readonly = input<boolean>();
  selectedCollarIds = input<number[] | undefined>();
  isFullScreen = input<boolean>();
  isAdmin = input<boolean>();
  showOverrideWarning = input<boolean>();
  notAvailableForIds = input<number[]>();

  remoteSchedules = toSignal(this.remoteCommandProviderService?.remoteSchedules$ ?? of());

  loadPositions = output<{
    deviceIds: number[];
    lastXRecords?: number;
    utcFrom?: string;
    utcTo?: string;
    isAnimation?: boolean;
  }>();

  defaultVirtualFenceCleared = input<boolean>();
  defaultVirtualFenceConstructionRules = input<webDtos.VirtualFenceConstructionRule[]>();
  readonlySubscription = toObservable(this.readonly)
    .pipe(takeUntilDestroyed())
    .subscribe((readonly) => {
      this.virtualFenceService.readonly.set(readonly);
    });

  hasVirtualFence = computed(
    () =>
      !this.readonly() ||
      this.defaultVirtualFenceConstructionRules()?.length ||
      this.virtualFenceService.constructionRules()?.length ||
      this.defaultVirtualFenceCleared() ||
      (!this.readonly() &&
        this.remoteSchedules()?.VIRTUAL_FENCE?.some((deviceId) =>
          this.selectedCollarIds()?.includes(deviceId),
        )),
  );

  updateDefaultSub = toObservable(this.defaultVirtualFenceConstructionRules)
    .pipe(
      takeUntilDestroyed(),
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
    )
    .subscribe(() => {
      this.createDefaultPolygons();
    });

  ngOnInit() {
    this.createDefaultPolygons();
  }

  ngOnDestroy() {
    this.virtualFenceService.destroyMap();
  }

  createDefaultPolygons() {
    if (this.defaultVirtualFenceConstructionRules()?.length) {
      const polygons = this.virtualFenceService.dataAsPolygonStructure([
        { virtualFenceConstructionRules: this.defaultVirtualFenceConstructionRules() },
      ]);
      this.virtualFenceService.clearAllPolygons();
      this.virtualFenceService.updateAndShowPolygons(polygons);
    }
  }
}
